import { z } from 'zod';
export const IdentityMaterialDTO = z.object({
    saltB64: z.string().min(1),
    saltRecoveryB64: z.string().min(1),
    rsaPubB64: z.string().min(1),
    wrappedPriv: z.string().min(1),
    wrappedPrivRecovery: z.string().min(1),
    verifierB64: z.string().min(1),
    verifierRecoveryB64: z.string().min(1),
});
export const RegisterRequest = IdentityMaterialDTO;
export const RegisterResponse = z.object({
    userId: z.string(),
    email: z.string().email(),
    createdAt: z.string(),
});
export const LoginBootstrapResponse = z.object({
    userId: z.string(),
    email: z.string().email(),
    saltB64: z.string(),
    rsaPubB64: z.string(),
    wrappedPriv: z.string(),
});
/**
 * Material needed to unlock the identity with the recovery code instead of the
 * passphrase. Deliberately does NOT carry `verifierRecoveryB64`: handing it out
 * would turn the endpoint into a free offline oracle. The client re-derives the
 * verifier from the code the user types, and the AES-GCM auth tag on
 * `wrappedPrivRecovery` is the authoritative check anyway.
 */
export const RecoveryBootstrapResponse = z.object({
    userId: z.string(),
    email: z.string().email(),
    saltRecoveryB64: z.string(),
    wrappedPrivRecovery: z.string(),
    rsaPubB64: z.string(),
});
export const VerifyPassphraseRequest = z.object({
    verifierB64: z.string().min(1),
});
export const VerifyPassphraseResponse = z.object({
    ok: z.boolean(),
});
const RecoveryRotation = z.object({
    saltRecoveryB64: z.string().min(1),
    wrappedPrivRecovery: z.string().min(1),
    verifierRecoveryB64: z.string().min(1),
});
/**
 * Proof that the caller knows the *current* key material. A valid JWT alone is
 * not enough: without this, a stolen token could overwrite `wrappedPriv` and
 * destroy the identity. The server checks it as a DynamoDB ConditionExpression,
 * so proof and write are a single atomic operation.
 */
export const ChangePassphraseProof = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('passphrase'), currentVerifierB64: z.string().min(1) }),
    z.object({ kind: z.literal('recovery'), currentVerifierRecoveryB64: z.string().min(1) }),
]);
export const ChangePassphraseRequest = z
    .object({
    saltB64: z.string().min(1),
    wrappedPriv: z.string().min(1),
    verifierB64: z.string().min(1),
    saltRecoveryB64: z.string().min(1).optional(),
    wrappedPrivRecovery: z.string().min(1).optional(),
    verifierRecoveryB64: z.string().min(1).optional(),
    proof: ChangePassphraseProof,
})
    .superRefine((value, ctx) => {
    const recoveryFields = [
        value.saltRecoveryB64,
        value.wrappedPrivRecovery,
        value.verifierRecoveryB64,
    ];
    const present = recoveryFields.filter((v) => v !== undefined).length;
    if (present !== 0 && present !== 3) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'recovery rotation requires all three fields (saltRecoveryB64, wrappedPrivRecovery, verifierRecoveryB64) or none',
        });
    }
    // Recovering with the code MUST rotate it: the same write that restores
    // access overwrites verifierRecoveryB64, which is what makes the code
    // single-use.
    if (value.proof.kind === 'recovery' && present !== 3) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'recovery-proof change must rotate the recovery material (all three fields)',
        });
    }
});
export const ChangePassphraseResponse = z.object({
    userId: z.string(),
    rotatedRecovery: z.boolean(),
    updatedAt: z.string(),
});
export const UserLookupResponse = z.object({
    userId: z.string(),
    email: z.string().email(),
    rsaPubB64: z.string(),
});
//# sourceMappingURL=auth.js.map